import pluralize from 'pluralize';
import React, { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
    formatDateAndTime,
    formatDurationToTime,
    formatDurationToHumanReadableString,
} from '../../utils/helpers';
import {
    getLocalizedBrandingSetting,
    interpolateBrandingText,
} from '../../utils/getLocalizedBrandingSetting';
import {
    Heading1,
    Heading4,
    Heading3,
    FlexDiv,
    FiftyFiftyDiv,
    TextDefault,
    ThemedButton,
    FocusArea,
} from '@awarego-ui/components';
import ThemedAssessmentIntroSVG from '../themed/ThemedAssessmentIntroSVG';
import { useStores } from '../../hooks/use-stores';

export default ({ onStart, assessment }) => {
    const { t, i18n } = useTranslation();

    const { brandingStore } = useStores();
    const branding = brandingStore?.branding;
    const languageCode = i18n.language;

    const introTitle = getLocalizedBrandingSetting(
        branding,
        'ui.assessments.intro_title',
        languageCode
    );
    const replacementIntroText = getLocalizedBrandingSetting(
        branding,
        'ui.assessments.intro_text',
        languageCode
    );
    const introTimerText = getLocalizedBrandingSetting(
        branding,
        'ui.assessments.intro_timer_text',
        languageCode
    );
    const introDueText = getLocalizedBrandingSetting(
        branding,
        'ui.assessments.intro_due_text',
        languageCode
    );
    const introStartButton = getLocalizedBrandingSetting(
        branding,
        'ui.assessments.intro_start_button',
        languageCode
    );
    const hideTimer = branding?.['ui.assessments.intro_hide_timer'] === '1';

    const durationTimeLabel = formatDurationToHumanReadableString(
        assessment?.duration
    );
    const formattedDueDate = assessment?.due
        ? formatDateAndTime(assessment.due)
        : '';

    const leftHalfContent = useMemo(() => {
        return (
            <>
                <Heading1 displayBlock>
                    {introTitle || assessment.name}
                </Heading1>
                <Heading3>
                    {assessment.definition.questions.length}{' '}
                    {t(
                        pluralize(
                            'question',
                            assessment.definition.questions.length
                        )
                    )}{' '}
                </Heading3>
                {assessment.due && (
                    <Heading4 mobileOnly>
                        <i className="date-icon due"></i>
                        {t('Due on')} {formattedDueDate} {t('GMT')}
                    </Heading4>
                )}

                {assessment.description && (
                    <Heading3 displayBlock>{assessment.description}</Heading3>
                )}
                {assessment.objective && (
                    <Heading3 displayBlock>{assessment.objective}</Heading3>
                )}
                <TextDefault style={{ whiteSpace: 'pre-wrap' }}>
                    {replacementIntroText ? (
                        replacementIntroText
                    ) : (
                        <Trans
                            components={{
                                b: <strong />,
                            }}
                        >
                            {t('assessment_intro_message_part_1', {
                                estimatedMinutes: Math.round(
                                    assessment.duration / 60
                                ),
                            })}
                        </Trans>
                    )}
                </TextDefault>
                {!hideTimer && (
                    <FocusArea
                        iconSrc="/static/img/icon-stopwatch.svg"
                        iconAltText={t('Time to complete')}
                        headingText={formatDurationToTime(assessment.duration)}
                        text={
                            introTimerText
                                ? interpolateBrandingText(introTimerText, {
                                      time: durationTimeLabel,
                                  })
                                : t('assessment_intro_time_to_complete', {
                                      time: durationTimeLabel,
                                      interpolation: { escapeValue: false },
                                  })
                        }
                    />
                )}
                {assessment.due && (
                    <TextDefault>
                        {introDueText ? (
                            interpolateBrandingText(introDueText, {
                                assessmentDueDate: formattedDueDate,
                            })
                        ) : (
                            <Trans
                                components={{
                                    b: <strong />,
                                }}
                            >
                                {t('assessment_intro_message_part_2', {
                                    assessmentDueDate: formattedDueDate,
                                })}
                            </Trans>
                        )}
                    </TextDefault>
                )}

                <FlexDiv desktopOnly>
                    <ThemedButton
                        mode="primary"
                        onClick={onStart}
                        text={introStartButton || t('Start now')}
                    ></ThemedButton>
                </FlexDiv>
            </>
        );
    }, [
        assessment,
        durationTimeLabel,
        formattedDueDate,
        hideTimer,
        introDueText,
        introStartButton,
        introTimerText,
        introTitle,
        onStart,
        replacementIntroText,
        t,
    ]);

    const rightHalf = useMemo(() => {
        return (
            <FlexDiv justifyCenter fullWidth>
                <ThemedAssessmentIntroSVG
                    primaryColor={brandingStore.theme.primary}
                />
            </FlexDiv>
        );
    }, [brandingStore]);

    return assessment != null ? (
        <FiftyFiftyDiv
            leftHalf={
                <>
                    {/* Needed to get the marginLeft for desktop only without adding more props to flexDiv */}
                    <FlexDiv column gap={16} mobileOnly>
                        {leftHalfContent}
                    </FlexDiv>
                    <FlexDiv column ml={64} gap={16} desktopOnly>
                        {leftHalfContent}
                    </FlexDiv>
                </>
            }
            rightHalf={rightHalf}
            stackOnMobile={true}
        />
    ) : null;
};
